export const SPECIAL_INSTRUCTIONS_MAX_LENGTH = 500;

// Mirrors quoting's pickup + delivery special instructions merge behavior.
// Idempotent: once delivery's text has been folded into pickup by a prior
// save, re-merging the same delivery text won't duplicate it.
export const formatSpecialInstructions = (
	pickupInstructions?: string | null,
	deliveryInstructions?: string | null,
): string => {
	const pickup = (pickupInstructions ?? "").trim();
	const delivery = (deliveryInstructions ?? "").trim();
	if (!delivery || pickup.includes(delivery)) {
		return pickup;
	}
	if (!pickup) {
		return delivery;
	}
	return [pickup, delivery].join("\n");
};

export type StopSpecialInstructions = {
	pickup: string;
	delivery: string;
};

// Inverse of formatSpecialInstructions: recovers pickup's own text from a saved merged value.
export const splitSpecialInstructions = (
	pickupInstructions?: string | null,
	deliveryInstructions?: string | null,
): StopSpecialInstructions => {
	const pickup = (pickupInstructions ?? "").trim();
	const delivery = (deliveryInstructions ?? "").trim();
	if (!delivery) {
		return { pickup, delivery };
	}
	if (pickup === delivery) {
		return { pickup: "", delivery };
	}
	const mergedSuffix = `\n${delivery}`;
	return {
		pickup: pickup.endsWith(mergedSuffix)
			? pickup.slice(0, -mergedSuffix.length).trim()
			: pickup,
		delivery,
	};
};

// Replaces one stop's own text and returns the new merged, length-capped box value.
export const rebuildSpecialInstructions = (
	current: StopSpecialInstructions | null,
	name: keyof StopSpecialInstructions,
	addressInstructions?: string | null,
): { stopInstructions: StopSpecialInstructions; merged: string } => {
	const stopInstructions = {
		pickup: current?.pickup ?? "",
		delivery: current?.delivery ?? "",
		[name]: (addressInstructions ?? "").trim(),
	};
	return {
		stopInstructions,
		merged: formatSpecialInstructions(
			stopInstructions.pickup,
			stopInstructions.delivery,
		).slice(0, SPECIAL_INSTRUCTIONS_MAX_LENGTH),
	};
};
